import { useRef } from 'react';
import axios, { CancelTokenSource } from 'axios';
import SORT_ORDER from 'src/enums/sortOrder';
import useFetchReducer from 'src/hooks/useFetchReducer';
import { FetchState } from 'src/reducers/fetchReducer';
import { HeaderFilters, SidebarFilters } from '../AlertResultsProvider';
import { ColumnSorting } from '../AlertResultsList/AlertResultsTable/AlertResultsTable.config';
import { AlertResult, AlertResultsList } from '../types/AlertResults';
import { getAlertResults } from '../api/alertResults.api';

export type AlertResultsListState = FetchState<AlertResult[]> & {
  total: number;
  page: number;
  offset: number;
  size: number;
};

type PropsUseAlertResultsListFetch = {
  headerFilters: HeaderFilters;
  sidebarFilters: SidebarFilters;
  sorting: ColumnSorting;
  page: number;
  pageSize: number;
  forcedReload: object | null;
};

const initialState: AlertResultsListState = {
  loading: false,
  error: false,
  data: null,
  total: 0,
  page: 1,
  offset: 1,
  size: 0,
};

const emptyAlertResults: AlertResultsList.Response = {
  alertResultList: [],
  totalRecords: '0',
  sortingorder: 'asc',
  sortingcolumn: 'sourceProductName',
  size: '10',
  offset: '1',
};

const useAlertResultsListFetch = ({
  headerFilters,
  sidebarFilters,
  sorting,
  page,
  pageSize,
  forcedReload,
}: PropsUseAlertResultsListFetch): [AlertResultsListState] => {
  const params: AlertResultsList.Request = {
    sortingOrder: SORT_ORDER[sorting.order],
    sortingColumn: sorting.field,
    size: pageSize,
    offset: page > 1 ? (page - 1) * pageSize + 1 : 1,
    sourceOwnerId: headerFilters.sourceSiteId,
    sourceCategoryId: headerFilters.sourceCategoryId,
    uniqueIdentifier: headerFilters.uniqueIdentifier,
    wml: headerFilters.wml,
    minVarience: headerFilters.variance?.[0] ?? null,
    maxVarience: headerFilters.variance?.[1] ?? null,
    alertId: headerFilters.alertId,
    sourceRatingCount: headerFilters.sourceRatingCount,
    createdDate: headerFilters.insertionDate,
    matchType: sidebarFilters.matchType,
    priceType: sidebarFilters.priceType,
    sourceRegionId: sidebarFilters.clientRegion,
    competitorOwnerId: sidebarFilters.competitorSiteId,
    competitorSellerName: sidebarFilters.competitorSellerName,
    competitorFulfillerName: sidebarFilters.competitorFulfillerName,
  };

  const fetchResults = async (params: AlertResultsList.Request): Promise<AlertResultsList.Response> => {
    if (!params.alertId || !params.sourceOwnerId || !params.createdDate) {
      return emptyAlertResults;
    }
    if (source.current) {
      source.current.cancel();
    }
    // TODO: we have to update `axios` library and use `AbortController` instead
    // This API is deprecated since `axios@0.22.0` but currently we use `axios@0.19.2`
    source.current = axios.CancelToken.source();
    return getAlertResults(params, source.current.token);
  };

  const source = useRef<CancelTokenSource | null>(null);
  const [{ loading, data, error }] = useFetchReducer(
    () => fetchResults(params),
    [...Object.values(params), forcedReload]
  );
  const state: AlertResultsListState = { ...initialState, loading, error };

  if (data === null) {
    return [state];
  }

  const { alertResultList, totalRecords } = data;

  return [
    {
      ...state,
      data: alertResultList,
      total: Number(totalRecords),
    },
  ];
};

export default useAlertResultsListFetch;
